/**
 * BITRO Configurator - Storefront-Client.
 *
 * Zustaendig fuer:
 *   - Config laden (/apps/bitro-configurator/config?product_id=...)
 *   - Defaults auswaehlen
 *   - Condition-Engine (show/hide Layer nach Regeln)
 *   - Render: gestapelte PNG-Vorschau (Stage) + Layer-Sidebar + Preisleiste
 *   - Interaktionen: Choice-Wahl, Text-Felder, Gruppen auf/zu
 *   - Lazy-Loading der Choice-Bilder (nur Default + Hovered werden eager geladen)
 *
 * NICHT in C2:
 *   - Canvas-Export und Add-to-Cart - kommt in C3.
 */
(function () {
  "use strict";

  /** @typedef {{
   *   id: number, name: string, description: string,
   *   imageUrl: string|null, thumbnailUrl: string|null,
   *   imageWidth: number|null, imageHeight: number|null,
   *   extraPriceCents: number, orderIndex: number, imageOrder?: number|null, isDefault: boolean,
   *   hasTextField: boolean, textFieldLabel: string|null,
   *   textFieldPlaceholder: string|null, textFieldMaxLength: number|null,
   *   textFieldRequired: boolean,
   *   isPriceActionTarget: boolean
   * }} Choice
   */
  /** @typedef {{
   *   id: number, parentId: number|null, name: string, description: string,
   *   type: 'group'|'simple', displayMode: string,
   *   orderIndex: number, imageOrder: number,
   *   canDeselect: boolean, allowMultiple: boolean, isDefaultActive: boolean,
   *   hideInCart: boolean, hideInSummary: boolean,
   *   isRandomizable: boolean, excludeFromRandomize: boolean,
   *   choices: Choice[]
   * }} Layer
   */
  /** @typedef {{
   *   id: number, name: string, relationship: 'AND'|'OR',
   *   rules: Array<{layerId:number|null, choiceId:number|null, state:string, action:string}>,
   *   actions: Array<{action:string, type:string, targetLayerId:number|null, targetChoiceId:number|null}>
   * }} Condition
   */
  /** @typedef {{
   *   product: { id:number, shopifyProductId:string, name:string, basePriceCents:number,
   *              priceActionEnabled:boolean, priceActionTargetCents:number,
   *              cartInfoMode:string },
   *   layers: Layer[],
   *   conditions: Condition[]
   * }} ConfigDTO
   */

  const root = document.getElementById("bitro-configurator-root");
  if (!root) return;

  const productId = root.dataset.productId;
  const productHandle = root.dataset.productHandle || "";
  const proxyBase = root.dataset.proxyBase || "/apps/bitro-configurator";
  const currency = root.dataset.currency || "EUR";
  const locale = root.dataset.locale || "de-DE";
  const shopifyVariantId = root.dataset.variantId || "";
  const basePriceFromTheme = Number(root.dataset.basePriceCents || 0);

  const getStorefrontClientId = () => {
    if (window.__bitroStorefrontClientId) return window.__bitroStorefrontClientId;
    const storageKey = "bitro_storefront_client_id";
    let id = "";
    try {
      id = window.localStorage.getItem(storageKey) || "";
      if (!/^[a-zA-Z0-9_-]{12,100}$/.test(id)) {
        id = window.crypto?.randomUUID?.() ||
          `bitro_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(storageKey, id);
      }
    } catch (_) {
      id = `bitro_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
    }
    window.__bitroStorefrontClientId = id;
    return id;
  };
  const storefrontClientId = getStorefrontClientId();

  const isBackorder = root.dataset.backorder === 'true';
  const expectedShipping = String(root.dataset.expectedShipping || '').trim();
  const backorderStatus = expectedShipping
    ? `Vorbestellung – aktuell nicht auf Lager; voraussichtlicher Versand ${expectedShipping}`
    : 'Vorbestellung – aktuell nicht auf Lager; Versand nach Wareneingang';

  // --- State -----------------------------------------------------------------

  /** @type {ConfigDTO|null} */
  let config = null;
  /** @type {Map<number, Set<number>>} layerId -> selected choice IDs */
  const selections = new Map();
  const selectedIds = (layerId) => selections.get(layerId) || new Set();
  const setSingleSelection = (layerId, choiceId) =>
    selections.set(layerId, new Set(choiceId ? [choiceId] : []));
  const selectedChoices = (layer) => layer.choices
    .filter((choice) => selectedIds(layer.id).has(choice.id))
    .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id);
  function* selectedEntries() {
    for (const [layerId, ids] of selections) {
      for (const choiceId of [...ids].sort((a, b) => a - b)) yield [layerId, choiceId];
    }
  }
  /** @type {Map<number, string>} choiceId -> text */
  const textValues = new Map();
  /** @type {Set<number>} expanded groups */
  const expandedGroups = new Set();
  const expandedLayers = new Set();
  /** @type {Set<number>} layers hidden by conditions */
  let hiddenLayers = new Set();
  /** @type {Set<number>} choices disabled by conditions (choiceId) */
  let disabledChoices = new Set();
  let forcedChoiceReasons = new Map();
  /** Choices whose component recipes are currently unavailable. */
  let inventoryUnavailableChoices = new Set();
  /** Unavailable choices that remain selectable because backorder is allowed. */
  let inventoryBackorderChoices = new Set();
  let inventoryBackorderNoticeEl = null;

  const applyInventoryStates = (payload) => {
    inventoryUnavailableChoices = new Set();
    inventoryBackorderChoices = new Set();
    for (const entry of payload?.choices || []) {
      const choiceId = Number(entry?.choiceId);
      if (!Number.isSafeInteger(choiceId) || choiceId <= 0) continue;
      const available = Number(entry?.available);
      const unavailable = entry?.available == null || !Number.isFinite(available) || available <= 0;
      if (!unavailable) continue;
      if (entry?.allowBackorder === true) inventoryBackorderChoices.add(choiceId);
      else inventoryUnavailableChoices.add(choiceId);
    }
  };

  const hasSelectedInventoryBackorder = () => {
    for (const [layerId, choiceId] of selectedEntries()) {
      if (hiddenLayers.has(layerId)) continue;
      if (choiceId && inventoryBackorderChoices.has(choiceId)) return true;
    }
    return false;
  };
  const currentCartButtonText = () =>
    isBackorder || hasSelectedInventoryBackorder() ? "Vorbestellen" : "In den Warenkorb";

  const updateInventoryBackorderNotice = () => {
    if (!inventoryBackorderNoticeEl || !config) return;
    const names = [];
    for (const layer of config.layers) {
      for (const choice of selectedChoices(layer)) {
        if (hiddenLayers.has(layer.id) || !inventoryBackorderChoices.has(choice.id)) continue;
        names.push(`${layer.name}: ${choice.name}`);
      }
    }
    inventoryBackorderNoticeEl.style.display = names.length ? "grid" : "none";
    const text = inventoryBackorderNoticeEl.querySelector(".bitro-backorder-notice__text");
    if (text) {
      text.textContent = names.length
        ? `${names.join(", ")} ist aktuell nicht auf Lager. Die Lieferung kann sich bis zum Wareneingang verzögern.`
        : "";
    }
    if (cartButtonEl && !cartButtonEl.disabled) cartButtonEl.textContent = currentCartButtonText();
  };

  const SENSITIVE_PROPERTY_RE =
    /(email|e-mail|mail|telefon|phone|tel|mobile|handy|name|vorname|nachname|address|adresse|street|strasse|stra\u00dfe|zip|plz|city|ort|kunde|customer|note|notiz|message|nachricht)/i;
  const errorReportCache = new Map();
  const ERROR_REPORT_DEDUPE_MS = 10 * 1000;

  const truncate = (value, max = 1000) => {
    const text = String(value ?? "");
    return text.length > max ? `${text.slice(0, max)}...[truncated]` : text;
  };

  const sanitizeProperties = (properties) => {
    if (!properties || typeof properties !== "object") return {};
    const out = {};
    for (const [key, value] of Object.entries(properties)) {
      out[key] = SENSITIVE_PROPERTY_RE.test(key) ? "[redacted]" : truncate(value, 2000);
    }
    return out;
  };

  const getSelectionSnapshot = () => {
    if (!config || !Array.isArray(config.layers)) return [];
    return config.layers
      .filter((layer) => layer.type === "simple")
      .flatMap((layer) => {
        const choices = selectedChoices(layer);
        return (choices.length ? choices : [null]).map((choice) => {
          return {
            layerId: layer.id,
            layerName: truncate(layer.name, 200),
            hidden: hiddenLayers.has(layer.id),
            choiceId: choice?.id || null,
            choiceName: truncate(choice?.name || "", 300),
            extraPriceCents: choice?.extraPriceCents || 0,
            text: choice?.hasTextField ? "[redacted]" : undefined,
          };
        });
      });
  };

  const errorPayload = (error) => ({
    name: truncate(error?.name || "Error", 120),
    message: truncate(error?.message || error, 1000),
    stack: truncate(error?.stack || "", 5000),
    status: error?.bitroStatus || null,
    response: error?.bitroResponse || null,
    properties: error?.bitroProperties || null,
  });

  const reportStorefrontError = (event, error, context = {}) => {
    try {
      const payload = {
        source: "bitro-configurator",
        event,
        clientId: storefrontClientId,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        productId,
        productHandle,
        variantId: shopifyVariantId,
        error: errorPayload(error),
        context: {
          totalCents: config ? calcTotalCents() : null,
          basePriceCents: basePriceFromTheme || config?.product?.basePriceCents || 0,
          selections: getSelectionSnapshot(),
          ...context,
        },
      };
      const cacheKey = `${event}|${window.location.pathname}|${payload.error.message}`;
      const now = Date.now();
      const last = errorReportCache.get(cacheKey) || 0;
      if (now - last < ERROR_REPORT_DEDUPE_MS) return;
      errorReportCache.set(cacheKey, now);

      const body = JSON.stringify(payload);
      const init = {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
      };
      if (body.length < 60000) init.keepalive = true;
      fetch(`${proxyBase}/client-error`, init).catch(() => {});
    } catch (_) {}
  };
  // --- Utilities -------------------------------------------------------------

  const formatPrice = (cents) => {
    return new Intl.NumberFormat(locale || "de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(cents / 100);
  };

  // Konfigurator-Aufpreise liegen in der Shop-Waehrung. Shopify stellt den
  // Varianten-Basispreis bereits in der Waehrung des aktuellen Marktes bereit.
  const toPresentmentCents = (shopCents) => {
    const value = Number(shopCents || 0);
    const shopBase = Number(config?.product?.basePriceCents || 0);
    if (
      !Number.isFinite(value) ||
      !Number.isFinite(shopBase) ||
      !Number.isFinite(basePriceFromTheme) ||
      shopBase <= 0 ||
      basePriceFromTheme <= 0
    ) {
      return Math.round(Number.isFinite(value) ? value : 0);
    }
    return Math.round((value * basePriceFromTheme) / shopBase);
  };

  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (k === "class") node.className = v;
      else if (k === "dataset") Object.assign(node.dataset, v);
      else if (k === "on") {
        for (const [evt, fn] of Object.entries(v)) {
          node.addEventListener(evt, fn);
        }
      } else if (k === "style" && typeof v === "object") {
        Object.assign(node.style, v);
      } else if (v !== undefined && v !== null && v !== false) {
        node.setAttribute(k, v === true ? "" : String(v));
      }
    }
    for (const c of [].concat(children)) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    }
    return node;
  };

  const byId = new Map();
  const rebuildByIdIndex = () => {
    byId.clear();
    if (!config) return;
    for (const layer of config.layers) {
      byId.set(`layer:${layer.id}`, layer);
      for (const choice of layer.choices) {
        byId.set(`choice:${choice.id}`, { choice, layer });
      }
    }
  };

  // --- Defaults --------------------------------------------------------------

  const radioGroupKey = (choice) => String(choice?.radioGroup || "").trim().toLocaleLowerCase("de");
  const addMultiSelection = (layer, choice, ids = selectedIds(layer.id)) => {
    const group = radioGroupKey(choice);
    if (group) {
      for (const peer of layer.choices) {
        if (peer.id !== choice.id && radioGroupKey(peer) === group) ids.delete(peer.id);
      }
    }
    ids.add(choice.id);
    return ids;
  };

  const applyDefaults = () => {
    if (!config) return;
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      const sorted = layer.choices
        .slice()
        .sort((a, b) => a.orderIndex - b.orderIndex);
      const def = sorted.find((c) => c.isDefault) ?? sorted[0];
      if (layer.allowMultiple) {
        const ids = new Set();
        for (const choice of sorted.filter((c) => c.isDefault)) addMultiSelection(layer, choice, ids);
        selections.set(layer.id, ids);
      }
      else setSingleSelection(layer.id, def ? def.id : null);
    }
  };

  // --- Condition-Engine ------------------------------------------------------

  const conditionLayerIds = (targetId) => {
    const ids = new Set();
    if (!byId.has(`layer:${targetId}`)) return ids;
    ids.add(targetId);
    for (let previous = -1; previous !== ids.size;) {
      previous = ids.size;
      for (const layer of config.layers) {
        if (layer.parentId != null && ids.has(layer.parentId)) ids.add(layer.id);
      }
    }
    return ids;
  };

  /** Liefert fuer Layer die Default-Choice (isDefault flag oder erste nach orderIndex). */
  const defaultChoiceIdFor = (layer) => {
    if (!layer || !layer.choices || layer.choices.length === 0) return null;
    const sorted = layer.choices
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .filter((choice) => !inventoryUnavailableChoices.has(choice.id));
    const def = sorted.find((c) => c.isDefault) ?? sorted[0];
    return def ? def.id : null;
  };

  /**
   * Evaluiert alle Conditions und mutiert ggf. `selections` (bei force-select / reset
   * nach disable). Kaskadiert bis stabil oder max. 5 Passes (gegen Endlosschleifen
   * bei sich gegenseitig triggernden Conditions).
   * @returns {{hiddenLayers: Set<number>, disabledChoices: Set<number>}}
   */
  const evaluateConditions = () => {
    if (!config) return { hiddenLayers: new Set(), disabledChoices: new Set(), forcedChoiceReasons: new Map() };

    let lastHidden = new Set();
    let lastDisabled = new Set();
    let lastReasons = new Map();

    for (let pass = 0; pass < 5; pass++) {
      const hidden = new Set();
      const shown = new Set();
      const disabled = new Set(inventoryUnavailableChoices);
      /** @type {Map<number, Set<number>>} layerId -> forced choice IDs */
      const forced = new Map();
      const reasons = new Map();

      for (const cond of config.conditions) {
        if (!cond.rules || cond.rules.length === 0) continue;

        const ruleMatches = cond.rules.map((rule) => {
          if (!rule.layerId || !byId.has(`layer:${rule.layerId}`)) return false;
          const isSelected = rule.choiceId && byId.get(`layer:${rule.layerId}`).type !== "group"
            ? selectedIds(rule.layerId).has(rule.choiceId)
            : [...conditionLayerIds(rule.layerId)].some(id => rule.choiceId
              ? selectedIds(id).has(rule.choiceId) : selectedIds(id).size > 0);
          // state === "1": loest aus wenn ausgewaehlt; state === "0": wenn NICHT ausgewaehlt
          const expectSelected = rule.state !== "0";
          return expectSelected ? isSelected : !isSelected;
        });

        const conditionMet =
          cond.relationship === "AND"
            ? ruleMatches.every(Boolean)
            : ruleMatches.some(Boolean);
        if (!conditionMet) continue;

        const causes = cond.rules.filter((_, index) => ruleMatches[index]).map(rule => {
          const layer = byId.get(`layer:${rule.layerId}`);
          if (rule.state === "0") return rule.choiceId
            ? `nicht ${byId.get(`choice:${rule.choiceId}`)?.choice.name || layer?.name}`
            : `keine Auswahl bei ${layer?.name}`;
          if (rule.choiceId) return byId.get(`choice:${rule.choiceId}`)?.choice.name;
          return [...conditionLayerIds(rule.layerId)].flatMap(id =>
            [...selectedIds(id)].map(cid => byId.get(`choice:${cid}`)?.choice.name)).filter(Boolean).join(", ");
        }).filter(Boolean);

        for (const action of cond.actions) {
          if (action.targetLayerId && !action.targetChoiceId &&
              (action.action === "disable" || (action.type === "choice" && action.action === "hide"))) {
            for (const id of conditionLayerIds(action.targetLayerId)) {
              for (const choice of byId.get(`layer:${id}`).choices) disabled.add(choice.id);
            }
            continue;
          }
          if (action.type === "layer" && action.targetLayerId) {
            if (action.action === "hide") hidden.add(action.targetLayerId);
            else if (action.action === "show") shown.add(action.targetLayerId);
          } else if (action.type === "choice" && action.targetChoiceId) {
            if (action.action === "disable" || action.action === "hide") {
              disabled.add(action.targetChoiceId);
            } else if (action.action === "select") {
              // Ziel-Layer aus der Action (importer legt beides ab) oder via byId ableiten
              const entry = byId.get(`choice:${action.targetChoiceId}`);
              const layerId = action.targetLayerId || (entry && entry.layer.id);
              if (layerId) {
                const ids = byId.get(`layer:${layerId}`)?.allowMultiple
                  ? forced.get(layerId) || new Set()
                  : new Set();
                if (entry && byId.get(`layer:${layerId}`)?.allowMultiple)
                  addMultiSelection(entry.layer, entry.choice, ids);
                else ids.add(action.targetChoiceId);
                forced.set(layerId, ids);
                if (causes.length) reasons.set(action.targetChoiceId,
                  `Erforderlich wegen: ${[...new Set(causes)].join(" + ")}.`);
              }
            }
          }
        }
      }

      // show hebt hide auf (wenn beide gleichzeitig feuern)
      for (const lid of shown) hidden.delete(lid);
      for (const lid of [...hidden]) for (const id of conditionLayerIds(lid)) hidden.add(id);

      // Selections korrigieren:
      // Single: force-select replaces the choice; multiple: adds the target checks.
      // Disabled multi choices are removed without substituting another service.
      let mutated = false;
      for (const [layerId, choiceIds] of forced.entries()) {
        for (const choiceId of choiceIds) {
          if (inventoryUnavailableChoices.has(choiceId)) continue;
          if (!selectedIds(layerId).has(choiceId)) {
            if (byId.get(`layer:${layerId}`)?.allowMultiple)
              addMultiSelection(byId.get(`layer:${layerId}`), byId.get(`choice:${choiceId}`).choice);
            else setSingleSelection(layerId, choiceId);
            mutated = true;
          }
        }
      }
      for (const [layerId, choiceId] of selectedEntries()) {
        if (!choiceId) continue;
        if (!disabled.has(choiceId)) continue;
        // Don't auto-reset wenn die Auswahl gerade durch force-select erzwungen wurde
        if (
          forced.get(layerId)?.has(choiceId) &&
          !inventoryUnavailableChoices.has(choiceId)
        )
          continue;
        const layer = byId.get(`layer:${layerId}`);
        if (layer?.allowMultiple) {
          selectedIds(layerId).delete(choiceId);
          mutated = true;
          continue;
        }
        const def = defaultChoiceIdFor(layer);
        // Falls auch Default disabled ist: auf null (keine Auswahl)
        const next = def && !disabled.has(def) ? def : null;
        if (next !== choiceId) {
          setSingleSelection(layerId, next);
          mutated = true;
        }
      }

      lastHidden = hidden;
      lastDisabled = disabled;
      lastReasons = new Map([...reasons].filter(([id]) => {
        const layerId = byId.get(`choice:${id}`)?.layer.id;
        return forced.get(layerId)?.has(id) && selectedIds(layerId).has(id) &&
          !hidden.has(layerId) && !inventoryUnavailableChoices.has(id);
      }));
      if (!mutated) break;
    }

    return { hiddenLayers: lastHidden, disabledChoices: lastDisabled, forcedChoiceReasons: lastReasons };
  };

  // --- Preis -----------------------------------------------------------------

  /**
   * Preis-Aufschluesselung:
   *   basePrice: Shopify-Basispreis des Produkts
   *   progressBaseCents: Basis + extras der NICHT-Aktions-Layer (fuer Preisaktion-Progress)
   *   actionUnlocked: true wenn Preisaktion aktiv UND progressBaseCents >= target
   *   actionExtras: maximaler Aktionsabzug fuer die aktuelle Auswahl
   *   totalCents: angezeigter Gesamt-Preis
   */
  // Keep in sync with price-action.shared.ts. Independent checkboxes do not share credit.
  const priceActionAllowance = (layer, choice, choices) => {
    if (choice.isPriceActionTarget) return Math.max(0, choice.extraPriceCents);
    const group = (choice.radioGroup || "").trim();
    if (layer.allowMultiple && !group) return 0;
    const target = choices
      .filter(c => c.isPriceActionTarget && c.extraPriceCents > 0 &&
        (!layer.allowMultiple || (c.radioGroup || "").trim() === group))
      .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id)[0];
    return Math.max(0, target?.extraPriceCents || 0);
  };

  const choiceActionDiscount = (layer, choice, unlocked) => unlocked
    ? Math.min(Math.max(0, toPresentmentCents(choice.extraPriceCents)),
      toPresentmentCents(priceActionAllowance(layer, choice, layer.choices)))
    : 0;

  const calcPrice = () => {
    if (!config) {
      return { basePrice: 0, progressBaseCents: 0, totalCents: 0, actionUnlocked: false, actionExtras: 0 };
    }
    const basePrice = basePriceFromTheme || config.product.basePriceCents || 0;
    let progressBaseCents = basePrice;
    let actionExtras = 0;
    let totalCents = basePrice;
    const conditional = [];
    for (const [layerId, choiceId] of selectedEntries()) {
      if (hiddenLayers.has(layerId)) continue;
      if (!choiceId) continue;
      const entry = byId.get(`choice:${choiceId}`);
      if (!entry) continue;
      if (entry.choice.priceRequiresPaidSelection && entry.choice.extraPriceCents > 0) {
        conditional.push(entry);
        continue;
      }
      const amount = toPresentmentCents(entry.choice.extraPriceCents);
      const allowance = toPresentmentCents(priceActionAllowance(entry.layer, entry.choice, entry.layer.choices));
      totalCents += amount;
      // An alternative in the promotional selection must not unlock its own credit.
      if (allowance > 0 && amount > 0) {
        actionExtras += Math.min(amount, allowance);
      } else {
        progressBaseCents += amount;
      }
    }
    const actionEnabled = !!config.product.priceActionEnabled;
    const target = toPresentmentCents(config.product.priceActionTargetCents);
    const actionUnlocked = actionEnabled && target > 0 && progressBaseCents >= target;
    if (actionUnlocked) totalCents -= actionExtras;
    // Match checkout-pricing.server: conditional extras never unlock one another.
    const conditionalSurchargeActive = totalCents > 0;
    if (conditionalSurchargeActive) {
      for (const { layer, choice } of conditional) {
        const credit = choiceActionDiscount(layer, choice, actionUnlocked);
        totalCents += toPresentmentCents(choice.extraPriceCents) - credit;
        if (actionUnlocked) actionExtras += credit;
      }
    }
    return { basePrice, progressBaseCents, totalCents, actionUnlocked, actionExtras, conditionalSurchargeActive };
  };

  const calcTotalCents = () => calcPrice().totalCents;

  // --- Selection-Listeners ---------------------------------------------------

  /** @type {Array<() => void>} */
  const changeListeners = [];
  const onChange = (fn) => changeListeners.push(fn);
  /** Track action-unlock-Transition, damit auto-select nur bei false->true greift
   *  und User-Entscheidungen beim Wieder-Lock nicht ueberschrieben werden. */
  let wasActionUnlocked = false;
  /** Layer-IDs bei denen die aktuelle Aktions-Choice vom Script automatisch
   *  gesetzt wurde (nicht manuell vom User) - wird beim Lock-Verlust auf Default
   *  zurueckgesetzt. Manuelle Klicks entfernen den Eintrag (dann ist's User-Wahl). */
  const autoSelectedLayerIds = new Set();

  /** Wenn Aktion unlocked: Layer mit einer Aktions-Choice werden automatisch
   *  auf diese Choice gesetzt (falls nicht schon). */
  const autoSelectActionChoices = () => {
    if (!config) return false;
    let changed = false;
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      if (hiddenLayers.has(layer.id)) continue;
      const actionChoices = (layer.choices || []).filter((c) => c.isPriceActionTarget);
      if (layer.allowMultiple) continue;
      if (actionChoices.length === 0) continue;
      const current = [...selectedIds(layer.id)][0];
      if (actionChoices.some((c) => c.id === current)) continue;
      const currentChoice = (layer.choices || []).find(c => c.id === current);
      if (currentChoice && currentChoice.extraPriceCents !== 0) continue;
      // Default: erste Aktions-Choice nehmen (Sort-Order orderIndex)
      const sorted = actionChoices.filter(c => !disabledChoices.has(c.id))
        .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id);
      if (!sorted.length) continue;
      setSingleSelection(layer.id, sorted[0].id);
      autoSelectedLayerIds.add(layer.id);
      changed = true;
    }
    return changed;
  };

  /** Lock verloren (Target nicht mehr erreicht): die Layer, die wir per auto-select
   *  gratis aktiviert hatten, zurueck auf Default. User-Entscheidungen (auto-Layer
   *  wurde danach manuell geaendert - ist dann nicht mehr im Set) bleiben. */
  const undoAutoSelectionsOnLock = () => {
    if (!config || autoSelectedLayerIds.size === 0) return false;
    let changed = false;
    for (const layerId of Array.from(autoSelectedLayerIds)) {
      const layer = byId.get(`layer:${layerId}`);
      if (!layer) continue;
      const defaultId = defaultChoiceIdFor(layer);
      if (!selectedIds(layerId).has(defaultId)) {
        setSingleSelection(layerId, defaultId);
        changed = true;
      }
    }
    autoSelectedLayerIds.clear();
    return changed;
  };

  const notify = () => {
    const result = evaluateConditions();
    hiddenLayers = result.hiddenLayers;
    disabledChoices = result.disabledChoices;
    forcedChoiceReasons = result.forcedChoiceReasons;

    // Preisaktion-Transitionen:
    //   false -> true: Aktions-Choices auto-aktivieren (User profitiert sofort) + Celebrate-Animation
    //   true -> false: vorher AUTO-aktivierte Choices auf Default zurueck (sonst
    //     bleibt ein ungewollter Kosten-Layer aktiv, obwohl die Aktion weg ist)
    const priceInfo = calcPrice();
    if (priceInfo.actionUnlocked && !wasActionUnlocked) {
      if (autoSelectActionChoices()) {
        const r2 = evaluateConditions();
        hiddenLayers = r2.hiddenLayers;
        disabledChoices = r2.disabledChoices;
        forcedChoiceReasons = r2.forcedChoiceReasons;
      }
      // Celebrate-Animation auf der Preisaktion-Box
      if (priceActionEl) {
        priceActionEl.classList.remove("bitro-price-action--celebrating");
        // reflow erzwingen damit die Animation bei re-trigger wirklich neu laeuft
        void priceActionEl.offsetWidth;
        priceActionEl.classList.add("bitro-price-action--celebrating");
        setTimeout(() => {
          priceActionEl && priceActionEl.classList.remove("bitro-price-action--celebrating");
        }, 1100);
      }
    } else if (!priceInfo.actionUnlocked && wasActionUnlocked) {
      if (undoAutoSelectionsOnLock()) {
        const r2 = evaluateConditions();
        hiddenLayers = r2.hiddenLayers;
        disabledChoices = r2.disabledChoices;
        forcedChoiceReasons = r2.forcedChoiceReasons;
      }
    }
    wasActionUnlocked = calcPrice().actionUnlocked;

    for (const l of changeListeners) l();
  };

  // --- Stage: gestapelte Bilder ---------------------------------------------

  let stageEl = null;

  const activeStageParts = () => {
    const activeLayers = [];
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      if (hiddenLayers.has(layer.id)) continue;
      for (const choice of selectedChoices(layer)) {
        if (!choice || !choice.imageUrl) continue;
        activeLayers.push({ layer, choice });
      }
    }
    // Choice overrides affect only image stacking, never the order of questions.
    activeLayers.sort((a, b) =>
      (a.choice.imageOrder ?? a.layer.imageOrder) - (b.choice.imageOrder ?? b.layer.imageOrder)
    );

    return activeLayers.map(({ layer, choice }, index) => ({
      key: `${layer.id}:${choice.id}`,
      type: "image",
      url: choice.imageUrl,
      alt: `${layer.name}: ${choice.name}`,
      zIndex: index,
      layer,
      choice,
    }));
  };

  const renderStage = () => {
    if (!stageEl) return;

    const parts = activeStageParts();

    const existing = new Map();
    for (const node of Array.from(stageEl.children)) {
      const key = node.dataset && node.dataset.bitroStageKey;
      if (key) existing.set(key, node);
    }

    const priceInfo = calcPrice();
    const keep = new Set();
    const newlyAppended = [];

    for (const part of parts) {
      let node = existing.get(part.key);

      if (!node) {
        node = el("img", {
          class: "bitro-stage__layer",
          src: part.url,
          alt: part.alt,
          loading: "eager",
          decoding: "async",
          dataset: { bitroStageKey: part.key },
        });
        newlyAppended.push(node);
        stageEl.appendChild(node);
      } else {
        node.alt = part.alt;
        if (node.src !== part.url) node.src = part.url;
      }
      node.style.zIndex = String(part.zIndex);
      const isActionFree =
        !!part.choice?.isPriceActionTarget && priceInfo.actionUnlocked && part.choice.extraPriceCents > 0;
      node.classList.toggle("bitro-stage__layer--action-free", isActionFree);
      keep.add(node);
    }

    for (const node of Array.from(stageEl.children)) {
      const isStagePart = node.classList && node.classList.contains("bitro-stage__layer");
      if (isStagePart && !keep.has(node)) node.remove();
    }

    const flashImg = (img) => {
      img.classList.remove("bitro-stage__layer--flash");
      void img.offsetWidth;
      img.classList.add("bitro-stage__layer--flash");
      setTimeout(() => img.classList.remove("bitro-stage__layer--flash"), 650);
    };

    let spinner = stageEl.querySelector(".bitro-stage__spinner");
    if (!spinner) {
      spinner = el("div", { class: "bitro-stage__spinner", "aria-hidden": "true" });
      stageEl.appendChild(spinner);
    }
    const pendingLoad = newlyAppended.filter((img) => !img.complete || img.naturalWidth === 0);
    const cachedImgs = newlyAppended.filter((img) => img.complete && img.naturalWidth > 0);

    if (pendingLoad.length === 0) {
      spinner.classList.remove("bitro-stage__spinner--visible");
    } else {
      spinner.classList.add("bitro-stage__spinner--visible");
      let remaining = pendingLoad.length;
      const done = () => {
        remaining--;
        if (remaining <= 0) spinner.classList.remove("bitro-stage__spinner--visible");
      };
      for (const img of pendingLoad) {
        img.addEventListener("load", () => { flashImg(img); done(); }, { once: true });
        img.addEventListener("error", done, { once: true });
      }
    }
    if (cachedImgs.length) {
      requestAnimationFrame(() => {
        for (const img of cachedImgs) flashImg(img);
      });
    }
  };

  // --- Sidebar: Layer-Baum ---------------------------------------------------

  let sidebarEl = null;
  const selectionErrors = new Map();

  const refreshSelectionErrors = () => {
    if (!selectionErrors.size) return;
    const missing = new Map([...validateRequiredRadioGroups(), ...validateRequiredSelections()]
      .map(issue => [issue.layerId, issue.message]));
    for (const id of selectionErrors.keys()) {
      if (missing.has(id)) selectionErrors.set(id, missing.get(id));
      else selectionErrors.delete(id);
    }
  };

  const renderSelectionError = (layer) => selectionErrors.has(layer.id)
    ? el("p", {
      id: `bitro-selection-error-${layer.id}`, class: "bitro-selection-error",
      role: "alert", tabindex: "-1",
    }, selectionErrors.get(layer.id))
    : null;

  const isInventoryDefaultOnlyLayer = (layer) => {
    if (layer.type !== "simple" || layer.allowMultiple || layer.choices.length < 2) return false;
    const sorted = layer.choices.slice().sort((a, b) => a.orderIndex - b.orderIndex);
    const defaultChoice = sorted.find(choice => choice.isDefault) ?? sorted[0];
    const available = sorted.filter(choice => !disabledChoices.has(choice.id));
    if (available.length !== 1 || !sorted.some(choice => inventoryUnavailableChoices.has(choice.id))) return false;
    const remaining = available[0];
    if (remaining.hasTextField) return false;
    const selected = selectedIds(layer.id);
    if (remaining.id === defaultChoice.id && selected.has(remaining.id)) return true;

    // Some optional layers have no explicit default: only a neutral "without" option remains.
    // Hide its UI only if choosing it cannot change price, preview, conditions or required input.
    if (remaining.extraPriceCents !== 0 || remaining.imageUrl ||
        (selected.size && !selected.has(remaining.id))) return false;
    return !config.layers.some(family => conditionLayerIds(family.id).has(layer.id) && (
      family.selectionRequired || config.conditions.some(condition => condition.rules?.some(rule => rule.layerId === family.id))
    ));
  };

  // Sidebar-only: keep defaults in the stage, price, conditions and cart payload.
  const inventoryHiddenSidebarLayers = (topLevel, childrenMap) => {
    const hidden = new Set();
    const visit = (layer) => {
      if (layer.type !== "group") {
        if (isInventoryDefaultOnlyLayer(layer)) hidden.add(layer.id);
        return;
      }
      const children = (childrenMap.get(layer.id) ?? []).filter(child => !hiddenLayers.has(child.id));
      children.forEach(visit);
      if (children.length && children.every(child => hidden.has(child.id))) hidden.add(layer.id);
    };
    topLevel.forEach(visit);
    return hidden;
  };

  const renderSidebar = () => {
    if (!sidebarEl) return;
    refreshSelectionErrors();
    sidebarEl.innerHTML = "";

    const topLevel = config.layers
      .filter((l) => l.parentId == null)
      .sort((a, b) => a.orderIndex - b.orderIndex);

    const childrenMap = new Map();
    for (const l of config.layers) {
      if (l.parentId != null) {
        const arr = childrenMap.get(l.parentId) ?? [];
        arr.push(l);
        childrenMap.set(l.parentId, arr);
      }
    }

    const inventoryHidden = inventoryHiddenSidebarLayers(topLevel, childrenMap);
    for (const top of topLevel) {
      if (hiddenLayers.has(top.id) || inventoryHidden.has(top.id)) continue;
      if (top.type === "group") {
        sidebarEl.appendChild(renderGroup(top, childrenMap, inventoryHidden));
      } else {
        if (isDisplayOnlyLayer(top)) continue;
        sidebarEl.appendChild(renderSimpleLayer(top));
      }
    }
  };

  const isDisplayOnlyLayer = (layer) =>
    layer.type === "simple" && !layer.allowMultiple && (layer.choices?.length ?? 0) <= 1;

  /**
   * Scrollt ein gerade aufgeklapptes Accordion-Header auf Mobile direkt unter
   * Stage + Header, damit der Nutzer am Anfang der Optionen landet.
   */
  const scrollAccordionHeaderIntoView = (headerEl) => {
    if (!headerEl || window.innerWidth >= 900) return;
    const stageWrap = document.querySelector(".bitro-layout__stage");
    const headerHeightVar = getComputedStyle(document.body).getPropertyValue("--header-height").trim();
    const headerOffset = parseInt(headerHeightVar, 10) || 0;
    const stageBottom = stageWrap ? stageWrap.getBoundingClientRect().bottom : headerOffset;
    const targetTop = headerEl.getBoundingClientRect().top;
    const delta = targetTop - stageBottom - 8; // 8px Padding
    if (Math.abs(delta) < 4) return;
    window.scrollBy({ top: delta, behavior: "smooth" });
  };

  /**
   * DOM-seitiger Accordion-Sync: toggelt nur die `--collapsed`-Klasse + Chevron
   * + aria-Attribute auf existierenden Elementen. So laeuft die CSS-Transition
   * (grid-template-rows), was bei einem kompletten renderSidebar() nicht ginge.
   */
  const applyAccordionState = () => {
    if (!sidebarEl) return;
    sidebarEl.querySelectorAll("[data-bitro-group-id]").forEach((groupEl) => {
      const gid = Number(groupEl.getAttribute("data-bitro-group-id"));
      const isOpen = expandedGroups.has(gid);
      const body = groupEl.querySelector(":scope > .bitro-group__body");
      const header = groupEl.querySelector(":scope > .bitro-group__header");
      const chevron = header && header.querySelector(".bitro-group__chevron");
      if (body) {
        body.classList.toggle("bitro-group__body--collapsed", !isOpen);
        body.setAttribute("aria-hidden", String(!isOpen));
      }
      if (header) header.setAttribute("aria-expanded", String(isOpen));
      if (chevron) chevron.textContent = isOpen ? "v" : ">";
    });
    sidebarEl.querySelectorAll("[data-bitro-layer-id]").forEach((layerEl) => {
      const lid = Number(layerEl.getAttribute("data-bitro-layer-id"));
      const isOpen = expandedLayers.has(lid);
      const body = layerEl.querySelector(":scope > .bitro-layer__body");
      const header = layerEl.querySelector(":scope > .bitro-layer__header");
      const chevron = header && header.querySelector(".bitro-layer__chevron");
      if (body) {
        body.classList.toggle("bitro-layer__body--collapsed", !isOpen);
        body.setAttribute("aria-hidden", String(!isOpen));
      }
      if (header) header.setAttribute("aria-expanded", String(isOpen));
      if (chevron) chevron.textContent = isOpen ? "v" : ">";
    });
  };

  /**
   * Sammelt alle Simple-Layer-Nachkommen einer Gruppe rekursiv.
   * Layer mit `excludeFromRandomize = true` werden uebersprungen - bei Sub-Gruppen
   * faellt implizit auch der ganze Teilbaum raus (wird nicht in die Queue aufgenommen).
   */
  const collectSimpleDescendants = (groupId) => {
    if (!config) return [];
    const out = [];
    const queue = [groupId];
    while (queue.length > 0) {
      const pid = queue.shift();
      for (const layer of config.layers) {
        if (layer.parentId !== pid) continue;
        if (layer.excludeFromRandomize) continue;
        if (layer.type === "group") queue.push(layer.id);
        else if (layer.type === "simple") out.push(layer);
      }
    }
    return out;
  };

  /**
   * Wuerfelt alle Simple-Layer einer Gruppe zufaellig neu. Ignoriert Conditions
   * absichtlich - die Haupt-Engine-Kaskade (evaluateConditions) korrigiert
   * invalide Kombinationen nach dem notify() automatisch.
   */
  const randomizeGroup = (groupId) => {
    const targets = collectSimpleDescendants(groupId);
    for (const layer of targets) {
      if (layer.allowMultiple) continue;
      const choices = (layer.choices || []).filter((c) => c && c.imageUrl);
      if (choices.length === 0) continue;
      const cur = [...selectedIds(layer.id)][0];
      // Bevorzuge eine andere Choice als die aktuelle, damit sichtbare Aenderung eintritt
      const others = choices.filter((c) => c.id !== cur);
      const pool = others.length > 0 ? others : choices;
      setSingleSelection(layer.id, pool[Math.floor(Math.random() * pool.length)].id);
    }
    notify();
  };

  const randomizerIcon = () => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "currentColor");
    svg.setAttribute("aria-hidden", "true");
    // Zauberstab mit Glanz-Sterne
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      "M7.5 5.6 10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zM19.5 15.4 17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a1 1 0 0 0-1.41 0L1.29 18.96a1 1 0 0 0 0 1.41l2.34 2.34a1 1 0 0 0 1.41 0L16.7 11.05a1 1 0 0 0 0-1.41zM13.13 10.5 5.53 18.1l-1.59-1.59 7.6-7.6z",
    );
    svg.appendChild(path);
    return svg;
  };

  const renderGroup = (group, childrenMap, inventoryHidden) => {
    const isOpen = expandedGroups.has(group.id);
    const children = (childrenMap.get(group.id) ?? [])
      .filter((c) => !hiddenLayers.has(c.id) && !inventoryHidden.has(c.id))
      .sort((a, b) => a.orderIndex - b.orderIndex);

    const header = el(
      "button",
      {
        type: "button",
        class: "bitro-group__header",
        "aria-expanded": String(isOpen),
        "aria-describedby": selectionErrors.has(group.id) ? `bitro-selection-error-${group.id}` : undefined,
        on: {
          click: () => {
            if (expandedGroups.has(group.id)) {
              expandedGroups.delete(group.id);
            } else {
              // Accordion: nur Geschwister-Gruppen auf gleicher Ebene schlieÃƒÆ’Ã…Â¸en,
              // Vorfahren bleiben offen damit die aktivierte Gruppe sichtbar ist.
              const siblingParent = group.parentId ?? null;
              for (const l of config.layers) {
                if (
                  l.type === "group" &&
                  (l.parentId ?? null) === siblingParent &&
                  l.id !== group.id
                ) {
                  expandedGroups.delete(l.id);
                }
              }
              expandedGroups.add(group.id);
              expandedLayers.clear();
            }
            applyAccordionState();
            // Nach CSS-Transition (220ms grid-template-rows): erst dann Scroll-Position
            // berechnen, sonst ist das Layout bei Auto-Close des alten Accordions
            // noch mid-transition und die Position verrutscht.
            const wasOpened = expandedGroups.has(group.id);
            if (wasOpened) {
              const groupEl = sidebarEl.querySelector(`[data-bitro-group-id="${group.id}"]`);
              const headerEl = groupEl && groupEl.querySelector(":scope > .bitro-group__header");
              setTimeout(() => scrollAccordionHeaderIntoView(headerEl), 260);
            }
          },
        },
      },
      [
        el("span", { class: "bitro-group__chevron" }, isOpen ? "v" : ">"),
        el("span", { class: "bitro-group__title" }, group.name),
        group.isRandomizable
          ? el(
              "span",
              {
                class: "bitro-group__randomizer",
                role: "button",
                tabindex: "0",
                title: "Zufallsvorschlag fuer diese Gruppe",
                "aria-label": "Zufallsvorschlag",
                on: {
                  click: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    randomizeGroup(group.id);
                  },
                  keydown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      randomizeGroup(group.id);
                    }
                  },
                },
              },
              [randomizerIcon()],
            )
          : null,
      ],
    );

    const inner = el(
      "div",
      { class: "bitro-group__body-inner" },
      children.map((c) =>
        c.type === "group"
          ? renderGroup(c, childrenMap, inventoryHidden)
          : renderSimpleLayer(c),
      ),
    );
    const body = el(
      "div",
      {
        class: "bitro-group__body" + (isOpen ? "" : " bitro-group__body--collapsed"),
        "aria-hidden": String(!isOpen),
      },
      [inner],
    );

    return el("div", { class: "bitro-group" + (selectionErrors.has(group.id) ? " bitro-selection--invalid" : ""), dataset: { bitroGroupId: String(group.id) } }, [header, renderSelectionError(group), body]);
  };

  const renderSimpleLayer = (layer) => {
    const isOpen = expandedLayers.has(layer.id);
    const currentChoices = selectedChoices(layer);

    const header = el(
      "button",
      {
        type: "button",
        class: "bitro-layer__header",
        "aria-expanded": String(isOpen),
        "aria-describedby": selectionErrors.has(layer.id) ? `bitro-selection-error-${layer.id}` : undefined,
        on: {
          click: () => {
            if (expandedLayers.has(layer.id)) {
              expandedLayers.delete(layer.id);
            } else {
              expandedLayers.clear();
              expandedLayers.add(layer.id);
            }
            applyAccordionState();
            const wasOpened = expandedLayers.has(layer.id);
            if (wasOpened) {
              const layerEl = sidebarEl.querySelector(`[data-bitro-layer-id="${layer.id}"]`);
              const headerEl = layerEl && layerEl.querySelector(":scope > .bitro-layer__header");
              setTimeout(() => scrollAccordionHeaderIntoView(headerEl), 260);
            }
          },
        },
      },
      [
        el("span", { class: "bitro-layer__chevron" }, isOpen ? "v" : ">"),
        el("span", { class: "bitro-layer__name" }, layer.name),
        currentChoices.length
          ? el("span", { class: "bitro-layer__selected" }, currentChoices.map((c) => c.name).join(", "))
          : el("span", { class: "bitro-layer__selected bitro-layer__selected--none" }, "-"),
      ],
    );

    const choicesGrid = el(
      "div",
      { class: "bitro-choices" + (layer.allowMultiple ? " bitro-choices--multiple" : "") },
      renderLayerChoices(layer),
    );

    const bodyChildren = [choicesGrid];
    for (const choice of currentChoices) {
      if (choice.hasTextField) bodyChildren.push(renderTextField(layer, choice));
    }
    if (layer.description) {
      bodyChildren.push(el("p", { class: "bitro-layer__description" }, layer.description));
    }
    const inner = el("div", { class: "bitro-layer__body-inner" }, bodyChildren);
    const body = el(
      "div",
      {
        class: "bitro-layer__body" + (isOpen ? "" : " bitro-layer__body--collapsed"),
        "aria-hidden": String(!isOpen),
      },
      [inner],
    );

    return el("div", { class: "bitro-layer" + (layer.allowMultiple ? " bitro-layer--multiple" : "") + (selectionErrors.has(layer.id) ? " bitro-selection--invalid" : ""), dataset: { bitroLayerId: String(layer.id) } }, [
      header,
      renderSelectionError(layer),
      body,
    ]);
  };

  const clearRadioGroup = (layer, group) => {
    for (const choice of layer.choices) {
      if (radioGroupKey(choice) === group) selectedIds(layer.id).delete(choice.id);
    }
  };

  // Evaluate the proposed switch, not the peer-disabling rule of the old choice.
  const canSelectRadio = (layer, group, choice = null) => {
    if (!choice && (layer.requiredRadioGroups || []).includes(group)) return false;
    if (choice && inventoryUnavailableChoices.has(choice.id)) return false;
    const saved = new Map([...selections].map(([id, ids]) => [id, new Set(ids)]));
    try {
      clearRadioGroup(layer, group);
      if (choice) addMultiSelection(layer, choice);
      const result = evaluateConditions();
      if (result.hiddenLayers.has(layer.id)) return false;
      if (!choice && layer.choices.filter(c => radioGroupKey(c) === group).every(c => result.disabledChoices.has(c.id))) return false;
      return choice
        ? selectedIds(layer.id).has(choice.id) && !result.disabledChoices.has(choice.id)
        : !layer.choices.some(c => radioGroupKey(c) === group && selectedIds(layer.id).has(c.id));
    } finally {
      selections.clear();
      for (const [id, ids] of saved) selections.set(id, ids);
    }
  };

  const renderLayerChoices = (layer) => {
    const visible = layer.choices.slice().sort((a, b) => a.orderIndex - b.orderIndex)
      .filter(c => !inventoryUnavailableChoices.has(c.id));
    const seen = new Set();
    return visible.map(choice => {
      const group = layer.allowMultiple ? radioGroupKey(choice) : "";
      if (!group) return [renderChoiceButton(layer, choice, selectedIds(layer.id).has(choice.id)), renderForcedChoiceHint(choice)];
      if (seen.has(group)) return null;
      seen.add(group);
      const groupId = String(seen.size);
      const members = visible.filter(c => radioGroupKey(c) === group);
      const anySelected = members.some(c => selectedIds(layer.id).has(c.id));
      const noneAllowed = canSelectRadio(layer, group);
      const noneLabel = layer.radioNoneLabels?.[group];
      const none = el("button", {
        type: "button", class: "bitro-choice bitro-choice--none" + (!anySelected ? " bitro-choice--selected" : ""),
        role: "radio", "aria-checked": String(!anySelected), disabled: !noneAllowed ? true : undefined,
        dataset: { bitroRadioNone: groupId },
        on: { click: (event) => {
          if (!noneAllowed) return;
          clearRadioGroup(layer, group);
          notify();
          if (event.detail === 0) sidebarEl.querySelector(`[data-bitro-layer-id="${layer.id}"] [data-bitro-radio-none="${groupId}"]`)?.focus({ preventScroll: true });
        } },
      }, [el("span", { class: "bitro-choice__check bitro-choice__check--radio", "aria-hidden": "true" }, ""),
        el("span", { class: "bitro-choice__label" }, typeof noneLabel === "string" && noneLabel.trim() || "Keine Auswahl")]);
      const required = (layer.requiredRadioGroups || []).includes(group);
      const buttons = [...(required ? [] : [none]), ...members.map(c => renderChoiceButton(layer, c, selectedIds(layer.id).has(c.id)))];
      const active = buttons.find(b => b.getAttribute("aria-checked") === "true" && !b.disabled)
        || buttons.find(b => !b.disabled);
      for (const button of buttons) button.setAttribute("tabindex", button === active ? "0" : "-1");
      return [el("div", { class: "bitro-radio-group", role: "radiogroup", "aria-label": choice.radioGroup.trim(),
        "aria-required": required ? "true" : undefined,
        on: { keydown: (event) => {
          if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          const enabled = buttons.filter(b => !b.disabled);
          const index = enabled.indexOf(event.target);
          if (index < 0) return;
          event.preventDefault();
          const next = event.key === "Home" ? 0 : event.key === "End" ? enabled.length - 1
            : (index + (["ArrowUp", "ArrowLeft"].includes(event.key) ? -1 : 1) + enabled.length) % enabled.length;
          enabled[next].click();
        } },
      }, buttons), ...members.map(renderForcedChoiceHint)];
    }).flat().filter(Boolean);
  };

  const renderForcedChoiceHint = (choice) => {
    const reason = forcedChoiceReasons.get(choice.id);
    return reason ? el("p", {
      id: `bitro-forced-hint-${choice.id}`, class: "bitro-forced-hint", role: "status",
    }, `${choice.name}: ${reason}`) : null;
  };

  const renderChoiceButton = (layer, choice, isSelected) => {
    const priceInfo = calcPrice();
    const conditionalInactive = choice.priceRequiresPaidSelection && choice.extraPriceCents > 0 && !priceInfo.conditionalSurchargeActive;
    const effectivePrice = toPresentmentCents(choice.extraPriceCents) -
      choiceActionDiscount(layer, choice, priceInfo.actionUnlocked);
    const actionFreeHere = !conditionalInactive && choice.extraPriceCents > 0 && effectivePrice === 0;
    const price = conditionalInactive
      ? `+${formatPrice(toPresentmentCents(choice.extraPriceCents))} bei weiterer Leistung`
      : actionFreeHere
      ? "gratis"
      : choice.extraPriceCents > 0
      ? `+${formatPrice(effectivePrice)}`
      : "";
    const group = layer.allowMultiple ? radioGroupKey(choice) : "";
    const isDisabled = group ? !canSelectRadio(layer, group, choice) : disabledChoices.has(choice.id);
    const isInventoryUnavailable = inventoryUnavailableChoices.has(choice.id);
    const isInventoryBackorder = inventoryBackorderChoices.has(choice.id);
    const classes =
      "bitro-choice" +
      (isSelected ? " bitro-choice--selected" : "") +
      (isDisabled ? " bitro-choice--disabled" : "");
    const thumbUrl = choice.thumbnailUrl || choice.imageUrl || "";
    return el(
      "button",
      {
        type: "button",
        class: classes,
        title:
          choice.name +
          (price ? ` ${price}` : "") +
          (isInventoryUnavailable ? " (nicht auf Lager)" : "") +
          (isInventoryBackorder ? " (Lieferrückstand möglich)" : "") +
          (isDisabled && !isInventoryUnavailable ? " (nicht verfügbar mit aktueller Auswahl)" : ""),
        dataset: { bitroChoiceId: String(choice.id) },
        role: layer.allowMultiple ? (group ? "radio" : "checkbox") : undefined,
        "aria-checked": layer.allowMultiple ? String(isSelected) : undefined,
        "aria-pressed": layer.allowMultiple ? undefined : String(isSelected),
        "aria-describedby": forcedChoiceReasons.has(choice.id) ? `bitro-forced-hint-${choice.id}` : undefined,
        "aria-disabled": isDisabled ? "true" : undefined,
        disabled: isDisabled ? true : undefined,
        on: {
          click: (event) => {
            if (isDisabled) return;
            if (isSelected && forcedChoiceReasons.has(choice.id)) {
              emphasizeForcedChoice(choice.id, true);
              return;
            }
            autoSelectedLayerIds.delete(layer.id);
            if (layer.allowMultiple) {
              const ids = selectedIds(layer.id);
              if (group) addMultiSelection(layer, choice, ids);
              else if (ids.has(choice.id)) ids.delete(choice.id);
              else ids.add(choice.id);
              selections.set(layer.id, ids);
            } else if (isSelected && layer.canDeselect) {
              setSingleSelection(layer.id, defaultChoiceIdFor(layer));
            } else {
              setSingleSelection(layer.id, choice.id);
            }
            notify();
            for (const selected of selectedChoices(layer)) {
              if (selected.id !== choice.id && forcedChoiceReasons.has(selected.id)) emphasizeForcedChoice(selected.id);
            }
            const current = sidebarEl.querySelector(`[data-bitro-choice-id="${choice.id}"]`);
            if (event.detail === 0) current?.focus({ preventScroll: true });
          },
        },
      },
      [
        layer.allowMultiple ? el("span", {
          class: "bitro-choice__check" + (group ? " bitro-choice__check--radio" : ""), "aria-hidden": "true",
        }, isSelected && !group ? "\u2713" : "") : null,
        thumbUrl
          ? el("img", {
              class: "bitro-choice__thumb",
              src: thumbUrl,
              alt: choice.name,
              loading: "lazy",
              decoding: "async",
            })
          : el("span", { class: "bitro-choice__thumb bitro-choice__thumb--empty" }, ""),
        el("span", { class: "bitro-choice__label" }, choice.name),
        isInventoryUnavailable
          ? el("span", { class: "bitro-choice__availability bitro-choice__availability--unavailable" }, "Nicht auf Lager")
          : isInventoryBackorder
            ? el("span", { class: "bitro-choice__availability bitro-choice__availability--backorder" }, "Lieferrückstand")
            : null,
        price
          ? el(
              "span",
              {
                class:
                  "bitro-choice__price" +
                  (actionFreeHere ? " bitro-choice__price--action" : ""),
              },
              price,
            )
          : null,
      ],
    );
  };

  const emphasizeForcedChoice = (choiceId, ensureVisible = false) => {
    const hint = sidebarEl?.querySelector(`#bitro-forced-hint-${choiceId}`);
    if (!hint) return;
    hint.classList.remove("bitro-forced-hint--highlight");
    void hint.offsetWidth;
    hint.classList.add("bitro-forced-hint--highlight");
    if (ensureVisible) {
      const stage = window.innerWidth < 900 && document.querySelector(".bitro-layout__stage");
      const top = Math.max(80, stage ? stage.getBoundingClientRect().bottom + 12 : 80);
      const bottom = window.innerHeight - 120;
      const rect = hint.getBoundingClientRect();
      const delta = rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0;
      if (delta) window.scrollBy({ top: delta, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    }
  };

  const renderTextField = (layer, choice) => {
    const value = textValues.get(choice.id) ?? "";
    const input = el("input", {
      type: "text",
      class: "bitro-textfield__input",
      value,
      placeholder: choice.textFieldPlaceholder || "",
      maxlength: choice.textFieldMaxLength ?? undefined,
      on: {
        input: (e) => {
          textValues.set(choice.id, e.target.value);
          // Keine Notify noetig - Text aendert nichts an Auswahl/Preis
        },
      },
    });
    return el("div", { class: "bitro-textfield" }, [
      el(
        "label",
        { class: "bitro-textfield__label" },
        choice.textFieldLabel || "Text",
      ),
      input,
      choice.textFieldRequired
        ? el("small", { class: "bitro-textfield__hint" }, "Pflichtfeld")
        : null,
    ]);
  };

  // --- Preisleiste -----------------------------------------------------------

  let priceEl = null;
  let cartButtonEl = null;
  let priceActionEl = null;

  /** ÃƒÆ’Ã…â€œberlagert den Shopify-Produkt-Basispreis auf der Produktseite durch den
   *  live-berechneten Konfigurator-Total. Zusaetzlich MutationObserver auf
   *  <product-price> damit Horizon-Re-Renders (variant-change) nicht zurueck-
   *  ueberschreiben. */
  let lastOverridePrice = null;
  let overrideObserver = null;
  let overrideObserverRewriting = false;

  const applyPriceOverride = (formatted) => {
    const host = document.querySelector("main") || document.body;
    if (!host || !formatted) return;

    // 1) Klassische Selektoren
    const priceSelectors = [
      ".price .price-item",
      ".price-item--sale",
      ".price-item--regular",
      ".price-item",
      ".price > span",
      "[data-product-price]",
    ].join(",");
    host.querySelectorAll(priceSelectors).forEach((el) => {
      if (el.closest("#bitro-configurator-root")) return;
      if (el.closest("s.compare-at-price, .compare-at-price")) return;
      if (el.classList && el.classList.contains("visually-hidden")) return;
      if (el.textContent && el.textContent.trim() === formatted) return;
      el.textContent = formatted;
    });

    // 2) Horizon <product-price> Web-Component - brute-force alle text-components
    //    und Money-looking <span>s darin ueberschreiben (exklusive visually-hidden und compare-at).
    host.querySelectorAll("product-price").forEach((pp) => {
      if (pp.closest("#bitro-configurator-root")) return;
      // text-components: sowohl value-Attribut als auch Textinhalt
      pp.querySelectorAll("text-component").forEach((tc) => {
        if (tc.closest("s.compare-at-price, .compare-at-price")) return;
        tc.textContent = formatted;
        if (tc.setAttribute) tc.setAttribute("value", formatted);
      });
      // Sichtbare Money-Spans (mit ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬, $, Ãƒâ€šÃ‚Â£ oder X,XX-Pattern)
      pp.querySelectorAll("span").forEach((sp) => {
        if (sp.classList && sp.classList.contains("visually-hidden")) return;
        if (sp.closest("s.compare-at-price, .compare-at-price")) return;
        const t = (sp.textContent || "").trim();
        if (!t) return;
        if (sp.children.length > 0) return; // Container - nur Leaf-Spans
        if (!/[\u20ac$\u00a3\u00a5]|[,.]\d{2}/.test(t)) return;
        if (t === formatted) return;
        sp.textContent = formatted;
      });
    });
  };

  const ensureOverrideObserver = () => {
    if (overrideObserver) return;
    const targets = document.querySelectorAll("product-price");
    if (!targets.length) return;
    overrideObserver = new MutationObserver(() => {
      if (overrideObserverRewriting || !lastOverridePrice) return;
      overrideObserverRewriting = true;
      try { applyPriceOverride(lastOverridePrice); }
      finally { setTimeout(() => { overrideObserverRewriting = false; }, 40); }
    });
    targets.forEach((t) => overrideObserver.observe(t, { childList: true, subtree: true, characterData: true }));
  };

  const overrideProductPageBasePrice = (formatted) => {
    if (!formatted) return;
    lastOverridePrice = formatted;
    applyPriceOverride(formatted);
    ensureOverrideObserver();
  };

  // Wenn Horizon spaeter Product-Price-Elemente nachlaedt (Section-Rendering),
  // Observer erneut attachen.
  document.addEventListener("variant:update", () => {
    if (lastOverridePrice) applyPriceOverride(lastOverridePrice);
  });
  document.addEventListener("variant:change", () => {
    if (lastOverridePrice) applyPriceOverride(lastOverridePrice);
  });

  const renderPrice = () => {
    if (!priceEl) return;
    const total = calcTotalCents();
    const formatted = formatPrice(total);
    priceEl.textContent = formatted;
    overrideProductPageBasePrice(formatted);
    renderPriceAction();
  };

  /** Liste der Aktions-Choice-Namen (fuer Progressbar-Text). */
  const priceActionLayerNames = () => {
    if (!config) return [];
    const names = [];
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      for (const choice of layer.choices || []) {
        if (choice.isPriceActionTarget) names.push(choice.name);
      }
    }
    return names;
  };

  const renderPriceAction = () => {
    if (!priceActionEl || !config) return;
    const actionEnabled = !!config.product.priceActionEnabled;
    const target = toPresentmentCents(config.product.priceActionTargetCents);
    if (!actionEnabled || target <= 0) {
      priceActionEl.style.display = "none";
      return;
    }
    priceActionEl.style.display = "";
    const { progressBaseCents, actionUnlocked, actionExtras } = calcPrice();
    const names = priceActionLayerNames();
    const namesStr = names.length > 0 ? names.join(" & ") : "Bonus-Layer";
    const pct = Math.max(0, Math.min(100, (progressBaseCents / target) * 100));
    const remaining = Math.max(0, target - progressBaseCents);
    const hasCreditedAlternative = actionUnlocked && config.layers.some(layer =>
      layer.type === "simple" && !hiddenLayers.has(layer.id) && selectedChoices(layer).some(choice =>
        !choice.isPriceActionTarget && choiceActionDiscount(layer, choice, true) > 0));
    const message = actionUnlocked
      ? hasCreditedAlternative ? `${formatPrice(actionExtras)} Aktionsvorteil angerechnet`
        : `OK ${namesStr} gratis inkludiert!`
      : `Noch ${formatPrice(remaining)} bis ${namesStr} gratis`;

    // --done-class togglen, aber andere transiente Klassen (--expanded, --celebrating)
    // nicht anfassen - sonst gehen State & Animation verloren.
    priceActionEl.classList.toggle("bitro-price-action--done", actionUnlocked);

    // DOM smart re-usen statt innerHTML="" - sonst verlieren wir
    //   a) die Animation beim fill-Balken (transition greift nur bei property-change am SELBEN element)
    //   b) die --celebrating-Class
    let labelEl = priceActionEl.querySelector(":scope > .bitro-price-action__label");
    let track = priceActionEl.querySelector(":scope > .bitro-price-action__track");
    let fill = track && track.querySelector(":scope > .bitro-price-action__fill");
    if (!labelEl) {
      labelEl = el("div", { class: "bitro-price-action__label" }, [
        el("span", { class: "bitro-price-action__label-text" }),
        (function () {
          const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          svg.setAttribute("viewBox", "0 0 24 24");
          svg.setAttribute("class", "bitro-price-action__chevron");
          svg.setAttribute("aria-hidden", "true");
          const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
          p.setAttribute("d", "M6 15l6-6 6 6");
          p.setAttribute("stroke", "currentColor");
          p.setAttribute("stroke-width", "2.2");
          p.setAttribute("stroke-linecap", "round");
          p.setAttribute("stroke-linejoin", "round");
          p.setAttribute("fill", "none");
          svg.appendChild(p);
          return svg;
        })(),
      ]);
      priceActionEl.appendChild(labelEl);
    }
    if (!track) {
      track = el("div", { class: "bitro-price-action__track" });
      fill = el("div", { class: "bitro-price-action__fill", style: { width: "0%" } });
      track.appendChild(fill);
      priceActionEl.appendChild(track);
      // Initial-width 0, im naechsten Frame auf pct damit die CSS-Transition laeuft
      requestAnimationFrame(() => { fill.style.width = pct.toFixed(1) + "%"; });
    } else {
      fill.style.width = pct.toFixed(1) + "%";
    }
    const labelTextEl = labelEl.querySelector(".bitro-price-action__label-text");
    if (labelTextEl && labelTextEl.textContent !== message) labelTextEl.textContent = message;
  };

  // --- Initial Layout --------------------------------------------------------

  const buildLayout = () => {
    root.innerHTML = "";

    const stage = el("div", { class: "bitro-stage" });
    stageEl = stage;

    const downloadBtn = el(
      "button",
      {
        type: "button",
        class: "bitro-stage__action",
        title: "Design als Bild herunterladen",
        "aria-label": "Design herunterladen",
        on: { click: () => handleDownloadDesign() },
      },
      [svgIcon("download")],
    );
    const shareBtn = el(
      "button",
      {
        type: "button",
        class: "bitro-stage__action",
        title: "Design teilen",
        "aria-label": "Design teilen",
        on: { click: () => handleShareDesign() },
      },
      [svgIcon("share")],
    );
    stage.appendChild(el("div", { class: "bitro-stage__actions" }, [downloadBtn, shareBtn]));

    const sidebar = el("div", { class: "bitro-sidebar" });
    sidebarEl = sidebar;

    // Preisaktion-Progressbar (nur wenn aktiviert) - auÃƒÆ’Ã…Â¸erhalb sidebar,
    // damit renderSidebar() sie nicht beim innerHTML-Clear wegwirft.
    if (config.product.priceActionEnabled && Number(config.product.priceActionTargetCents || 0) > 0) {
      priceActionEl = el("div", { class: "bitro-price-action" });
      // Mobile-Tap: im stuck-State togglet ein Klick das Label ein/aus - damit
      // der User sich die "Noch XÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ bis..."-Info auch nachtraeglich holen kann.
      priceActionEl.addEventListener("click", () => {
        if (!root.classList.contains("bitro-configurator--stuck")) return;
        priceActionEl.classList.toggle("bitro-price-action--expanded");
      });
    }

    const priceLabel = el("div", { class: "bitro-bar__label" }, "Gesamtpreis");
    const priceVal = el("div", { class: "bitro-bar__price" }, formatPrice(0));
    priceEl = priceVal;

    const cartButton = el(
      "button",
      {
        type: "button",
        class: "bitro-bar__cart",
        on: {
          click: () => {
            handleAddToCart().catch((err) => {
              reportStorefrontError("configurator_add_to_cart_failed", err);
              setCartBusy(false, err.message || "Fehler beim Hinzufuegen");
            });
          },
        },
      },
      "In den Warenkorb",
    );
    cartButtonEl = cartButton;
    cartButton.textContent = currentCartButtonText();

    const bar = el("div", { class: "bitro-bar" }, [
      el("div", { class: "bitro-bar__left" }, [priceLabel, priceVal]),
      cartButton,
    ]);

    const sidebarWrap = el("div", { class: "bitro-layout__sidebar" }, [sidebar]);
    const stageWrap = el("div", { class: "bitro-layout__stage" }, [stage]);
    // Preisaktion-Balken haengt IMMER unter dem Vorschaubild im Stage-Wrapper -
    // auf Desktop nutzt das den freien Platz unter dem Stage, auf Mobile klebt es
    // sticky mit dem Stage zusammen.
    if (priceActionEl) stageWrap.appendChild(priceActionEl);
    const layout = el("div", { class: "bitro-layout" }, [stageWrap, sidebarWrap]);

    inventoryBackorderNoticeEl = el(
      "div",
      {
        class: "bitro-backorder-notice bitro-backorder-notice--choice",
        role: "status",
        style: { display: "none" },
      },
      [
        el("strong", { class: "bitro-backorder-notice__heading" }, "Lieferhinweis"),
        el("span", { class: "bitro-backorder-notice__text" }, ""),
      ],
    );
    root.appendChild(inventoryBackorderNoticeEl);

    if (isBackorder) {
      const noticeText = expectedShipping
        ? `Dieser Artikel ist momentan nicht auf Lager. Voraussichtlicher Versand ${expectedShipping}. Mit deiner Bestellung akzeptierst du die längere Lieferzeit.`
        : 'Dieser Artikel ist momentan nicht auf Lager. Der Versand erfolgt nach Wareneingang. Mit deiner Bestellung akzeptierst du die längere Lieferzeit.';
      root.appendChild(
        el('div', { class: 'bitro-backorder-notice', role: 'status' }, [
          el('strong', { class: 'bitro-backorder-notice__heading' }, '⚠️ Vorbestellung'),
          el('span', {}, noticeText),
        ]),
      );
    }

    // Stuck-Detection: misst OB stageWrap als sticky-Container am Header klebt.
    // Wenn stageWrap.top <= header-height, dann sitzt es am sticky-Anker fest ->
    // der User hat gerade ueber das Vorschaubild hinausgescrollt. Hysterese ueber
    // verschiedene Grenzwerte rein/raus verhindert Feedback-Loop mit margin-top.
    const STUCK_IN_PX = 4;    // stuck aktiv sobald stageWrap am Header klebt (<= 4px Gap)
    const STUCK_OUT_PX = 28;  // stuck inaktiv wenn wieder > 28px Luft zum Header
    let isStuck = false;
    const getHeaderHeight = () => {
      const raw = getComputedStyle(document.body).getPropertyValue("--header-height").trim();
      return parseFloat(raw) || 0;
    };
    const checkStuck = () => {
      const headerH = getHeaderHeight();
      const gap = stageWrap.getBoundingClientRect().top - headerH;
      if (!isStuck && gap <= STUCK_IN_PX) {
        isStuck = true;
        root.classList.add("bitro-configurator--stuck");
      } else if (isStuck && gap > STUCK_OUT_PX) {
        isStuck = false;
        root.classList.remove("bitro-configurator--stuck");
      }
    };
    window.addEventListener("scroll", checkStuck, { passive: true });
    window.addEventListener("resize", checkStuck);
    checkStuck();

    root.appendChild(layout);
    if (config.product.serviceNotice) {
      root.appendChild(el(
        "p",
        { class: "bitro-service-notice" },
        config.product.serviceNotice + " Die Einsendeanleitung erh\u00e4ltst du nach Bezahlung per E-Mail.",
      ));
    }
    root.appendChild(bar);

    // Default: nur die erste Top-Level-Gruppe offen (Accordion)
    const topGroups = config.layers
      .filter((l) => l.parentId == null && l.type === "group")
      .sort((a, b) => a.orderIndex - b.orderIndex);
    if (topGroups.length > 0) expandedGroups.add(topGroups[0].id);

    // Default: der erste sichtbare Simple-Layer in Render-Reihenfolge ist offen
    const orderedSelectable = [];
    const collectSelectable = (parentId) => {
      const kids = config.layers
        .filter((l) => (l.parentId ?? null) === parentId)
        .sort((a, b) => a.orderIndex - b.orderIndex);
      for (const k of kids) {
        if (k.type === "group") collectSelectable(k.id);
        else if (!isDisplayOnlyLayer(k)) orderedSelectable.push(k);
      }
    };
    collectSelectable(null);
    if (orderedSelectable.length > 0) expandedLayers.add(orderedSelectable[0].id);
  };

  // --- Cart: Canvas-Export + Upload + Add-to-Cart ---------------------------

  const svgIcon = (name) => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "currentColor");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    if (name === "download") {
      path.setAttribute("d", "M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z");
    } else if (name === "share") {
      path.setAttribute("d", "M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z");
    }
    svg.appendChild(path);
    return svg;
  };

  let toastTimer = null;
  const showToast = (message) => {
    let toast = root.querySelector(".bitro-toast");
    if (!toast) {
      toast = el("div", { class: "bitro-toast", role: "status", "aria-live": "polite" });
      root.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add("bitro-toast--visible");
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove("bitro-toast--visible"), 2500);
  };

  const encodeSelectionsForUrl = () => {
    const compact = [];
    for (const [lid, cid] of selectedEntries()) {
      if (hiddenLayers.has(lid) || !cid) continue;
      compact.push({ l: lid, c: cid, t: textValues.get(cid) || undefined });
    }
    const json = JSON.stringify(compact);
    const bytes = new TextEncoder().encode(json);
    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  };

  const buildShareUrl = () => {
    const encoded = encodeSelectionsForUrl();
    const path = productHandle ? `/products/${productHandle}` : window.location.pathname;
    return `${window.location.origin}${path}?bitro_share=${encoded}`;
  };

  const handleDownloadDesign = async () => {
    try {
      showToast("Bild wird erstellt...");
      const blob = await renderCompositeImage();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (config.product.name || "konfiguration").replace(/[^a-z0-9-_]+/gi, "-");
      a.download = `bitro-${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast("Bild heruntergeladen");
    } catch (err) {
      reportStorefrontError("configurator_download_failed", err);
      showToast("Download fehlgeschlagen");
    }
  };

  const handleShareDesign = async () => {
    const url = buildShareUrl();
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mein BITRO-Design", url });
        return;
      } catch (_) {
        // User hat abgebrochen -> fallback auf Clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast("Link in die Zwischenablage kopiert");
    } catch (_) {
      window.prompt("Link kopieren:", url);
    }
  };

  let restoreLineKey = null;

  const decodeSelectionsFromUrl = (encoded) => {
    try {
      let b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (err) {
      reportStorefrontError("configurator_share_decode_failed", err);
      return null;
    }
  };

  const applyCompactSelections = (compact) => {
    if (!Array.isArray(compact)) return;
    // The saved list is complete: an omitted optional multi-layer means no checks.
    for (const layer of config.layers) {
      if (layer.allowMultiple) selections.set(layer.id, new Set());
    }
    for (const entry of compact) {
      if (!entry || typeof entry.l !== "number" || typeof entry.c !== "number") continue;
      const layer = config.layers.find((l) => l.id === entry.l);
      if (!layer || layer.type !== "simple") continue;
      const choice = layer.choices.find((c) => c.id === entry.c);
      if (!choice) continue;
      if (layer.allowMultiple) addMultiSelection(layer, choice);
      else setSingleSelection(layer.id, choice.id);
      if (choice.hasTextField && typeof entry.t === "string") {
        textValues.set(choice.id, entry.t);
      }
    }
  };

  const applyUrlRestore = async () => {
    const params = new URLSearchParams(window.location.search);
    const shareEncoded = params.get("bitro_share");
    const restoreKey = params.get("bitro_restore");

    if (shareEncoded) {
      const compact = decodeSelectionsFromUrl(shareEncoded);
      if (compact) applyCompactSelections(compact);
      return;
    }
    if (restoreKey) {
      try {
        const res = await fetch("/cart.js", { credentials: "include" });
        if (!res.ok) return;
        const cart = await res.json();
        const item = cart.items.find((i) => i.key === restoreKey);
        if (!item || !item.properties) return;
        const raw = item.properties._bitro_selections;
        if (!raw) return;
        const compact = JSON.parse(raw);
        applyCompactSelections(compact);
        restoreLineKey = restoreKey;
      } catch (err) {
        reportStorefrontError("configurator_restore_failed", err, { restoreLineKey: restoreKey });
      }
    }
  };

  const setCartBusy = (busy, message) => {
    if (!cartButtonEl) return;
    cartButtonEl.disabled = busy;
    if (busy) {
      cartButtonEl.setAttribute("aria-busy", "true");
    } else {
      cartButtonEl.removeAttribute("aria-busy");
    }
    cartButtonEl.textContent = busy
      ? message || "Einen Moment..."
      : message || currentCartButtonText();
  };

  // Browser back/forward can restore this page from the BFCache with the
  // pre-navigation DOM state. Always clear the transient add-to-cart state.
  window.addEventListener("pageshow", () => {
    setCartBusy(false, currentCartButtonText());
  });

  /** Laedt ein Bild mit CORS, damit wir es auf das Canvas zeichnen duerfen.
   *  Cache-Bust-Param umgeht Browser-Cached-Versionen die ohne CORS-Header geladen wurden. */
  const loadImageForCanvas = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Bild konnte nicht geladen werden: ${url}`));
      // Kein Cache-Bust wenn URL bereits Query hat - Shopify CDN liefert CORS-Header korrekt
      img.src = url;
    });

  /** Rendert die aktuell sichtbare Konfiguration in ein Canvas und gibt einen PNG-Blob zurueck. */
  const renderCompositeImage = async (options = {}) => {
    const parts = activeStageParts();
    const imageParts = parts.filter((part) => part.type === "image" && part.url);
    if (imageParts.length === 0) {
      throw new Error("Keine Auswahl getroffen - bitte waehle mindestens einen Layer");
    }

    let canvasW = 0;
    let canvasH = 0;
    const loadedParts = await Promise.all(imageParts.map(async (part) => ({ ...part, img: await loadImageForCanvas(part.url) })));
    for (const part of loadedParts) {
      if (part.img.naturalWidth > canvasW) canvasW = part.img.naturalWidth;
      if (part.img.naturalHeight > canvasH) canvasH = part.img.naturalHeight;
    }
    if (!canvasW || !canvasH) {
      canvasW = 2048;
      canvasH = 1371;
    }

    const maxDimension = Number(options.maxDimension || 0);
    if (Number.isFinite(maxDimension) && maxDimension > 0) {
      const largestSide = Math.max(canvasW, canvasH);
      if (largestSide > maxDimension) {
        const scale = maxDimension / largestSide;
        canvasW = Math.max(1, Math.round(canvasW * scale));
        canvasH = Math.max(1, Math.round(canvasH * scale));
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    for (const part of loadedParts) {
      ctx.drawImage(part.img, 0, 0, canvasW, canvasH);
    }
return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Canvas-Export fehlgeschlagen"))),
        "image/png",
        0.92,
      );
    });
  };

  // Mirrors cart-properties.shared.ts for the standalone storefront asset.
  const cartSelectionAttributes = (name, values) => {
    const chunks = [];
    for (const value of values) {
      const text = value.slice(0, 1024);
      const index = chunks.length - 1;
      if (index >= 0 && chunks[index].length + text.length + 2 <= 1024) chunks[index] += "; " + text;
      else chunks.push(text);
    }
    return chunks.map((value, i) => ({
      key: name.slice(0, 240) + (i ? ` (${i + 1})` : ""),
      value,
    }));
  };

  /** Sammelt die Line-Item-Properties (lesbar + versteckt). */
  const buildLineItemProperties = (priceAdjustmentCents) => {
    const props = {};
    const priceInfo = calcPrice();
    const showAllCartInfo = config.product.cartInfoMode === "all";

    // Gewaehlte Layer als lesbare Properties (Label: Wert).
    // Standard: nur Abweichungen vom Default. Optional pro Produkt: alle sichtbaren Layer.
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      if (hiddenLayers.has(layer.id)) continue;
      if (layer.hideInCart) continue;
      const values = [];
      for (const choice of selectedChoices(layer)) {
        const cid = choice.id;

        const sorted = layer.choices
          .slice()
          .sort((a, b) => a.orderIndex - b.orderIndex);
        const defaultChoice = sorted.find((c) => c.isDefault) ?? sorted[0];
        const isDefault = defaultChoice && defaultChoice.id === cid;

        const conditionalInactive = choice.priceRequiresPaidSelection && choice.extraPriceCents > 0 && !priceInfo.conditionalSurchargeActive;
        const effectivePrice = conditionalInactive ? 0 : toPresentmentCents(choice.extraPriceCents) -
          choiceActionDiscount(layer, choice, priceInfo.actionUnlocked);
        const actionFreeHere = !conditionalInactive && choice.extraPriceCents > 0 && effectivePrice === 0;

        let value = choice.name;
        if (choice.extraPriceCents > 0) {
          value += conditionalInactive ? " (ohne Aufpreis)" : actionFreeHere
            ? ` (gratis durch Preisaktion)`
            : ` (+${formatPrice(effectivePrice)})`;
        } else if (choice.extraPriceCents < 0) {
          value += ` (${formatPrice(toPresentmentCents(choice.extraPriceCents))})`;
        }
        let text = "";
        if (choice.hasTextField) {
          text = (textValues.get(choice.id) || "").trim();
          if (text) value += ` - "${text}"`;
        }

        if (
          !showAllCartInfo &&
          !layer.allowMultiple &&
          isDefault &&
          !text &&
          choice.extraPriceCents === 0
        ) {
          continue;
        }
        values.push(value);
      }
      for (const entry of cartSelectionAttributes(layer.name, values)) props[entry.key] = entry.value;
    }

    if (isBackorder) {
      props["Lieferstatus"] = backorderStatus;
    } else if (hasSelectedInventoryBackorder()) {
      props["Lieferstatus"] =
        "Mindestens eine Auswahl ist aktuell nicht auf Lager. Lieferung nach Wareneingang.";
    }

    if (config.product.serviceNotice) {
      props["Leistung"] = config.product.serviceNotice;
    }

    // Versteckte Properties (underscore-Prefix -> nicht im Checkout sichtbar)
    props["_bitro_surcharge_cents"] = String(priceAdjustmentCents);
    props["_bitro_product_id"] = String(config.product.id);

    // Komprimierte Auswahl fuer evtl. Re-Hydration
    const compact = [];
    for (const [lid, cid] of selectedEntries()) {
      if (hiddenLayers.has(lid) || !cid) continue;
      compact.push({
        l: lid,
        c: cid,
        t: textValues.get(cid) || undefined,
      });
    }
    props["_bitro_selections"] = JSON.stringify(compact);

    return props;
  };

  /** Cart-AJAX: fuegt das Produkt mit allen Properties hinzu. */
  const addToCart = async (properties) => {
    const body = {
      items: [
        {
          id: Number(shopifyVariantId),
          quantity: 1,
          properties,
        },
      ],
    };
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      const cartError = new Error(`Cart-Add fehlgeschlagen (${response.status}): ${text.slice(0, 200)}`);
      cartError.bitroStatus = response.status;
      cartError.bitroResponse = truncate(text, 1000);
      cartError.bitroProperties = sanitizeProperties(properties);
      throw cartError;
    }
    return response.json();
  };

  /** Pflichtfelder pruefen (Text-Felder mit required=true). */
  const validateRequiredTextFields = () => {
    const missing = [];
    for (const layer of config.layers) {
      if (layer.type !== "simple") continue;
      if (hiddenLayers.has(layer.id)) continue;
      for (const choice of selectedChoices(layer)) {
        if (!choice || !choice.hasTextField || !choice.textFieldRequired)
          continue;
        const text = (textValues.get(choice.id) || "").trim();
        if (!text) missing.push(`${layer.name} / ${choice.name} - Text fehlt`);
      }
    }
    return missing;
  };

  const validateRequiredRadioGroups = () => {
    const missing = [];
    for (const layer of config.layers) {
      if (!layer.allowMultiple || hiddenLayers.has(layer.id)) continue;
      for (const group of layer.requiredRadioGroups || []) {
        const members = layer.choices.filter(c => radioGroupKey(c) === group);
        if (!members.length || members.some(c => selectedIds(layer.id).has(c.id))) continue;
        // A conditional prerequisite may temporarily disable the whole group.
        // Stock alone must not silently turn a mandatory service into an optional one.
        if (members.every(c => disabledChoices.has(c.id) && !inventoryUnavailableChoices.has(c.id))) continue;
        missing.push({ layerId: layer.id, message: "Bitte eine Option ausw\u00e4hlen." });
      }
    }
    return missing;
  };

  const validateRequiredSelections = () => {
    const missing = [];
    for (const layer of config.layers) {
      if (!layer.selectionRequired || hiddenLayers.has(layer.id)) continue;
      const scope = conditionLayerIds(layer.id);
      const hasSelection = config.layers.some(member =>
        scope.has(member.id) && member.type === "simple" &&
        !hiddenLayers.has(member.id) && !isDisplayOnlyLayer(member) &&
        member.choices.some(choice => selectedIds(member.id).has(choice.id) && !disabledChoices.has(choice.id))
      );
      if (!hasSelection) missing.push({ layerId: layer.id, message: "Bitte mindestens eine Option ausw\u00e4hlen." });
    }
    return missing;
  };

  const showSelectionErrors = (issues) => {
    selectionErrors.clear();
    for (const issue of issues) selectionErrors.set(issue.layerId, issue.message);
    const visited = new Set();
    let layer = byId.get("layer:" + issues[0].layerId);
    while (layer && !visited.has(layer.id)) {
      visited.add(layer.id);
      (layer.type === "group" ? expandedGroups : expandedLayers).add(layer.id);
      layer = byId.get("layer:" + layer.parentId);
    }
    renderSidebar();
    // Wait for accordion layout, then keep the hint below the sticky mobile preview.
    setTimeout(() => {
      const hint = sidebarEl?.querySelector(`#bitro-selection-error-${issues[0].layerId}`);
      if (!hint) return;
      hint.focus({ preventScroll: true });
      const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue("--header-height"), 10) || 0;
      const stage = window.innerWidth < 900 && document.querySelector(".bitro-layout__stage");
      const top = Math.max(headerHeight, stage ? stage.getBoundingClientRect().bottom : 0) + 16;
      const rect = hint.getBoundingClientRect();
      if (rect.top < top || rect.bottom > window.innerHeight - 100) {
        window.scrollBy({ top: rect.top - top, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
      }
    }, 260);
  };

  const handleAddToCart = async () => {
    const missingSelections = [...validateRequiredRadioGroups(), ...validateRequiredSelections()];
    if (missingSelections.length) {
      showSelectionErrors(missingSelections);
      return;
    }
    const missing = validateRequiredTextFields();
    if (missing.length > 0) {
      alert("Bitte Pflichtfelder ausfuellen:\n" + missing.join("\n"));
      return;
    }

    if (config.product.serviceNotice && calcTotalCents() <= 0) {
      alert("Bitte waehle mindestens einen Umbau aus.");
      return;
    }

    setCartBusy(true, "Warenkorb wird aktualisiert...");
    const priceAdjustmentCents =
      calcTotalCents() -
      (basePriceFromTheme || config.product.basePriceCents || 0);
    const properties = buildLineItemProperties(priceAdjustmentCents);

    if (restoreLineKey) {
      try {
        await fetch("/cart/change.js", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: restoreLineKey, quantity: 0 }),
        });
      } catch (err) {
        reportStorefrontError("configurator_old_line_remove_failed", err, { restoreLineKey });
      }
    }

    await addToCart(properties);

    // Zur Cart-Seite umleiten (oder Toast + Theme-Cart-Drawer oeffnen, falls Theme das unterstuetzt)
    window.location.href = "/cart";
  };

  // --- Startup ---------------------------------------------------------------

  // Ring-Umfang fuer r=52: C = 2*PI*52 ~= 326.73. Offset = C*(1-pct/100).
  const RING_CIRCUMFERENCE = 326.73;
  const setLoadingProgress = (pct) => {
    const clamped = Math.max(0, Math.min(100, Math.round(pct)));
    const pctEl = root.querySelector("[data-bitro-percent]");
    const ringFg = root.querySelector(".bitro-loading__ring-fg");
    if (pctEl) pctEl.textContent = clamped + "%";
    if (ringFg) ringFg.setAttribute("stroke-dashoffset", String(RING_CIRCUMFERENCE * (1 - clamped / 100)));
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const retryAfterMs = (value) => {
    if (!value) return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const dateMs = Date.parse(value);
    if (Number.isFinite(dateMs)) return Math.max(0, dateMs - Date.now());
    return null;
  };

  const retryDelayMs = (attempt, status, retryAfter) => {
    if (retryAfter != null) return Math.min(15000, Math.max(1000, retryAfter));
    if (status === 429) return [1000, 2500, 5000, 8000, 12000][attempt - 1] || 12000;
    return [500, 1200, 2500, 5000, 8000][attempt - 1] || 8000;
  };

  const isRetryableConfigError = (status) => {
    return !status || status === 408 || status === 425 || status === 429 || status >= 500;
  };

  const loadConfigWithRetry = async (url) => {
    let lastError = null;
    const maxAttempts = 6;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      let status = null;
      let retryAfter = null;
      try {
        const response = await fetch(url, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        status = response.status;
        retryAfter = retryAfterMs(response.headers.get("Retry-After"));
        if (!response.ok) {
          const err = new Error(`Config nicht geladen: HTTP ${response.status}`);
          err.bitroStatus = response.status;
          throw err;
        }
        return response.json();
      } catch (err) {
        lastError = err;
        status = err && err.bitroStatus ? err.bitroStatus : status;
        if (attempt >= maxAttempts || !isRetryableConfigError(status)) break;
        setLoadingProgress(Math.min(35, 15 + attempt * 4));
        await wait(retryDelayMs(attempt, status, retryAfter));
      }
    }
    throw lastError || new Error("Config nicht geladen");
  };

  const start = async () => {
    setLoadingProgress(5);
    try {
      const url = `${proxyBase}/config?product_id=${encodeURIComponent(productId)}`;
      setLoadingProgress(15);
      config = await loadConfigWithRetry(url);
      applyInventoryStates(config.inventory);
      setLoadingProgress(40);
    } catch (err) {
      reportStorefrontError("configurator_config_load_failed", err);
      root.innerHTML = `<div class="bitro-configurator__error">Konfigurator konnte nicht geladen werden.</div>`;
      return;
    }

    rebuildByIdIndex();
    applyDefaults();
    // Keep a lone checklist immediately visible, including with a static image layer.
    const selectableLayers = config.layers.filter((layer) => layer.type === "simple" && !isDisplayOnlyLayer(layer));
    if (selectableLayers.length === 1 && selectableLayers[0].allowMultiple && selectableLayers[0].parentId == null) {
      expandedLayers.add(selectableLayers[0].id);
    }
    await applyUrlRestore();
    ({ hiddenLayers, disabledChoices, forcedChoiceReasons } = evaluateConditions());
    // Initial: wenn Preisaktion schon beim Load unlocked ist (teureres Base-Produkt,
    // URL-Restore mit vielen Extras), Aktions-Choices sofort setzen - sonst wuerde
    // Progressbar "Gratis inkludiert!" zeigen aber die Choice waere nicht selektiert.
    const initialPrice = calcPrice();
    if (initialPrice.actionUnlocked) {
      autoSelectActionChoices();
      ({ hiddenLayers, disabledChoices, forcedChoiceReasons } = evaluateConditions());
    }
    wasActionUnlocked = calcPrice().actionUnlocked;
    setLoadingProgress(55);

    // Primaere Layer-Bilder preloaden - so kommt der User nie zu einem leeren Stage
    const primaryUrls = activeStageParts()
      .filter((part) => part.type === "image" && part.url)
      .map((part) => part.url);

    if (primaryUrls.length) {
      let loaded = 0;
      const total = primaryUrls.length;
      await Promise.all(primaryUrls.map((src) => new Promise((resolve) => {
        const img = new Image();
        img.onload = img.onerror = () => {
          loaded++;
          setLoadingProgress(55 + Math.round((loaded / total) * 35));
          resolve();
        };
        img.src = src;
      })));
    }
    setLoadingProgress(92);

    buildLayout();
    renderStage();
    renderSidebar();
    renderPrice();
    updateInventoryBackorderNotice();
    setLoadingProgress(100);

    onChange(() => {
      renderStage();
      renderSidebar();
      renderPrice();
      updateInventoryBackorderNotice();
    });
  };

  start();
})();
